import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useEnableFemaTenantMutation,
  useRotateFemaTenantMutation,
  useValidateFemaTenantMutation,
} from 'services/ipaws/tenants/femaTenants.api';
import {
  FEMA_TENANT_UNEXPECTED_ANSWER,
  inspectFemaCredential,
  storeFemaCredential,
  toFemaTenantRefusal,
  type FemaCredentialFiles,
} from 'services/ipaws/tenants/femaTenants.service';
import type {
  FemaCredentialInspection,
  FemaCredentialStoreAnswer,
  FemaTenantRefusal,
  FemaTenantSummary,
  FemaTenantValidation,
} from 'types/femaTenant.types';
import type { FemaUploadStep } from '../../../config/femaCredentials.config';

export type FemaPasswordField = 'keystorePassword' | 'privateKeyPassword';

type PasswordValues = Record<FemaPasswordField, string>;
type PasswordFilled = Record<FemaPasswordField, boolean>;

const EMPTY_PASSWORDS: PasswordValues = { keystorePassword: '', privateKeyPassword: '' };
const NOTHING_FILLED: PasswordFilled = { keystorePassword: false, privateKeyPassword: false };

/**
 * The FE-11 upload stepper (design read §3.4): File → Passwords → Review → Validate → Enabled.
 *
 * PASSWORDS: the values live in `passwordsRef` (a component ref) and in the uncontrolled
 * password inputs only. They never go to React state, Redux or browser storage. `clearPasswords`
 * empties the ref and the inputs after EACH call (inspect and store), after an error, when the
 * person goes back, and on unmount. The page never refills them, so the Review step asks for them
 * again before the store call.
 */
export const useCredentialUpload = (tenant: FemaTenantSummary) => {
  const [step, setStep] = useState<FemaUploadStep>('File');
  const [files, setFiles] = useState<FemaCredentialFiles | null>(null);
  const [keyAlias, setKeyAlias] = useState('');
  const [filled, setFilled] = useState<PasswordFilled>(NOTHING_FILLED);
  const [inspection, setInspection] = useState<FemaCredentialInspection | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [stored, setStored] = useState<FemaCredentialStoreAnswer | null>(null);
  const [validation, setValidation] = useState<FemaTenantValidation | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<FemaTenantRefusal | null>(null);
  const [busy, setBusy] = useState(false);

  const passwordsRef = useRef<PasswordValues>({ ...EMPTY_PASSWORDS });
  const keystorePasswordField = useRef<HTMLInputElement | null>(null);
  const privateKeyPasswordField = useRef<HTMLInputElement | null>(null);

  const [validateTenant] = useValidateFemaTenantMutation();
  const [enableTenant] = useEnableFemaTenantMutation();
  const [rotateTenant] = useRotateFemaTenantMutation();

  const clearPasswords = useCallback(() => {
    passwordsRef.current = { ...EMPTY_PASSWORDS };
    if (keystorePasswordField.current) keystorePasswordField.current.value = '';
    if (privateKeyPasswordField.current) privateKeyPasswordField.current.value = '';
    setFilled(NOTHING_FILLED);
  }, []);

  // Unmount: the ref goes away with the component, but empty it first so no closure keeps a value.
  useEffect(
    () => () => {
      passwordsRef.current = { ...EMPTY_PASSWORDS };
    },
    [],
  );

  const setPassword = useCallback((field: FemaPasswordField, value: string) => {
    passwordsRef.current = { ...passwordsRef.current, [field]: value };
    setFilled((current) =>
      current[field] === value.length > 0 ? current : { ...current, [field]: value.length > 0 },
    );
  }, []);

  const takeSecrets = () => {
    const { keystorePassword, privateKeyPassword } = passwordsRef.current;
    return {
      ...(keystorePassword ? { keystorePassword } : {}),
      ...(privateKeyPassword ? { privateKeyPassword } : {}),
    };
  };

  const chooseFiles = (next: FemaCredentialFiles) => {
    setFiles(next);
    setError(null);
    setStep('Passwords');
  };

  const back = () => {
    clearPasswords();
    setError(null);
    setConfirmed(false);
    if (step === 'Passwords') setStep('File');
    if (step === 'Review') {
      setInspection(null);
      setStep('Passwords');
    }
  };

  const inspect = async () => {
    if (!files) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      const answer = await inspectFemaCredential({
        tenantId: tenant.id,
        files,
        secrets: takeSecrets(),
        ...(keyAlias ? { keyAlias } : {}),
        onProgress: setProgress,
      });
      setInspection(answer.inspection);
      setConfirmed(false);
      setStep('Review');
    } catch (failure) {
      setError(toFemaTenantRefusal(failure));
    } finally {
      clearPasswords();
      setProgress(null);
      setBusy(false);
    }
  };

  const store = async () => {
    if (!files || !inspection || !confirmed) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      const answer = await storeFemaCredential({
        tenantId: tenant.id,
        files,
        secrets: takeSecrets(),
        ...(keyAlias ? { keyAlias } : {}),
        confirmSha256: inspection.sha256,
        onProgress: setProgress,
      });
      setStored(answer);
      setStep('Validate');
    } catch (failure) {
      setError(toFemaTenantRefusal(failure));
    } finally {
      clearPasswords();
      setProgress(null);
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!stored) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await validateTenant({
        tenantId: tenant.id,
        slot: stored.credential.slot,
      }).unwrap();
      if (answer) setValidation(answer.validation);
      else setError(FEMA_TENANT_UNEXPECTED_ANSWER);
    } catch (failure) {
      setError(toFemaTenantRefusal(failure));
    } finally {
      setBusy(false);
    }
  };

  /** A replacement is swapped in (rotate); a first credential is enabled. */
  const finish = async () => {
    if (!stored || validation?.state !== 'passed') return;
    setBusy(true);
    setError(null);
    try {
      // A `null` answer is malformed: the step stays, and the contract refusal shows.
      const answer =
        stored.credential.slot === 'pending'
          ? await rotateTenant(tenant.id).unwrap()
          : stored.tenant.enabled
            ? stored.tenant
            : await enableTenant(tenant.id).unwrap();
      if (answer) setStep('Enabled');
      else setError(FEMA_TENANT_UNEXPECTED_ANSWER);
    } catch (failure) {
      setError(toFemaTenantRefusal(failure));
    } finally {
      setBusy(false);
    }
  };

  return {
    step,
    files,
    keyAlias,
    setKeyAlias,
    filled,
    inspection,
    confirmed,
    setConfirmed,
    stored,
    validation,
    progress,
    error,
    busy,
    passwordFields: {
      keystorePassword: keystorePasswordField,
      privateKeyPassword: privateKeyPasswordField,
    },
    setPassword,
    chooseFiles,
    back,
    inspect,
    store,
    validate,
    finish,
  };
};

export type CredentialUploadState = ReturnType<typeof useCredentialUpload>;
